import { Col, Flex, Row, Text } from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import * as React from 'react';
import { components } from 'react-select';
import {
  ContainerOrderTransportItemStatus,
  DateField,
  SelectField,
  TextField,
} from '~/common/components';
import { Trans, useLocale } from '~/common/context';
import { ITransportItemStatus } from '~/common/types';
import { ValueInfoItem } from '~/purchase/components';
import { TRANSPORT_ITEM_STATUS_LABEL_LOOKUP } from '~/common/translators';
import type { IContainerOrder } from '~/common/types';

type WriteDisplayProps = {
  item: NonNullable<IContainerOrder['resolvedItems']>[number];
  currency?: string;
};

export const WriteDisplay: React.FC<WriteDisplayProps> = ({
  item,
  currency,
}) => {
  const locale = useLocale();
  const statusOptions: Array<{ value: ITransportItemStatus; label: string }> =
    React.useMemo(
      () =>
        Object.entries(TRANSPORT_ITEM_STATUS_LABEL_LOOKUP).map(
          ([value, labelKey]) => ({
            value: value as ITransportItemStatus,
            label: labelKey,
          }),
        ),
      [],
    );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomStatusOption = React.useCallback((props: any) => {
    const { data } = props;
    return (
      <components.Option
        {...props}
        className={'d-flex align-items-center space-x-2 position-relative'}
      >
        <ContainerOrderTransportItemStatus
          status={data.value as ITransportItemStatus}
          size='xxs'
        />
      </components.Option>
    );
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomStatusSingleValue = React.useCallback((props: any) => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        {
          <ContainerOrderTransportItemStatus
            status={data.value as ITransportItemStatus}
            size='xxs'
          />
        }
      </components.SingleValue>
    );
  }, []);

  return (
    <>
      <Row className='gy-3'>
        <Col xs={12} sm={3}>
          <TextField
            name='reference'
            label={
              <Trans
                i18nKey='app.container.order.detail.container_number'
                values={{ number: item.reference || '-' }}
              />
            }
          />
          <Text
            heading='sm'
            weight='semi-bold'
            color='secondary-gray'
            id={`container-id-${item.reference}`}
          >
            <Trans
              i18nKey='app.container.order.detail.container_id'
              values={{ id: item.id }}
            />
          </Text>
        </Col>
        <Col xs={12} sm={2}>
          <SelectField<false, { value: ITransportItemStatus; label: string }>
            name='status'
            label={<Trans i18nKey='app.common.status' />}
            options={statusOptions}
            isMulti={false}
            components={{
              Option: CustomStatusOption,
              SingleValue: CustomStatusSingleValue,
            }}
            renderReadMode={(option) => (
              <>
                {option ? (
                  <ContainerOrderTransportItemStatus status={option.value} />
                ) : (
                  '-'
                )}
              </>
            )}
          />
        </Col>
        <Col xs={12} sm={7}>
          <Flex
            className='w-100 space-x-2'
            alignItems='center'
            justifyContent='between'
          >
            <DateField
              name='deliveryDate'
              dateFormat={DateUtils.DATE_FORMAT.DMY}
              showTimeSelect={false}
              label={
                <Trans i18nKey='app.purchase.orders.table.delivery_date' />
              }
              minDate={new Date()}
            />

            <ValueInfoItem
              as='div'
              title={<Trans i18nKey='app.common.total_quantity' />}
              align='right'
            >
              <Text
                heading='normal'
                weight='semi-bold'
                color='secondary'
                className='mt-2 text-right'
                id={`quantity-${item.reference}`}
              >
                {locale.formatters.quantity(item.totalQuantity ?? 0)}
              </Text>
            </ValueInfoItem>
            <ValueInfoItem
              as='div'
              title={<Trans i18nKey='app.common.total_price' />}
              align='right'
            >
              <Text
                heading='normal'
                weight='semi-bold'
                color='secondary'
                className='mt-2 text-right'
                id={`quantity-${item.reference}`}
              >
                {locale.formatters.money(item.totalPrice ?? 0, {
                  currency: currency,
                })}
              </Text>
            </ValueInfoItem>
          </Flex>
        </Col>
      </Row>
    </>
  );
};
